import { useMemo } from 'react';
import { TreeNode } from './TreeNode';

export interface UseTreeSelectionOptions {
  selectedTopics: any[];
  isSelecting: boolean;
  isTopicDisabled: (topicData: any) => boolean;
}

export function useTreeSelection({ selectedTopics, isSelecting, isTopicDisabled }: UseTreeSelectionOptions) {
  const isTopicSelected = useMemo(() => {
    return (topicData: any): boolean => {
      const topicId = topicData.e179_TopicId || topicData.e180_TopicId;
      return selectedTopics.some(selectedTopic => 
        (selectedTopic.e179_TopicId === topicId) || 
        (selectedTopic.e180_TopicId === topicId)
      );
    };
  }, [selectedTopics]);

  const getLeafTopicNodes = useMemo(() => {
    return (node: TreeNode): TreeNode[] => {
      const leafNodes: TreeNode[] = [];
      
      if (node.type === 'topic' && node.data) {
        // Check if this is a leaf topic (no child topics)
        const hasChildTopics = (node.data.e179_ChildTopics && node.data.e179_ChildTopics.length > 0) ||
                              (node.data.e180_ChildTopics && node.data.e180_ChildTopics.length > 0);
        if (!hasChildTopics) {
          leafNodes.push(node);
          return leafNodes;
        }
      }
      
      // For non-leaf topics or year nodes, recursively get leaf nodes from children
      if (node.children) {
        for (const child of node.children) {
          leafNodes.push(...getLeafTopicNodes(child));
        }
      }
      
      return leafNodes;
    };
  }, []);

  const getCheckboxState = useMemo(() => {
    return (node: TreeNode): 'checked' | 'unchecked' | 'indeterminate' => {
      if (!isSelecting) return 'unchecked';
      
      const leafNodes = getLeafTopicNodes(node);
      if (leafNodes.length === 0) return 'unchecked';
      
      let selectedCount = 0;
      let disabledCount = 0;
      
      for (const leafNode of leafNodes) {
        if (leafNode.data) {
          if (isTopicDisabled(leafNode.data)) {
            disabledCount++;
            // Disabled topics are considered selected (they're already in student curriculum)
            selectedCount++;
          } else if (isTopicSelected(leafNode.data)) {
            selectedCount++;
          }
        }
      }
      
      if (selectedCount === 0) return 'unchecked';
      if (selectedCount === leafNodes.length) return 'checked';
      return 'indeterminate';
    };
  }, [isSelecting, getLeafTopicNodes, isTopicSelected, isTopicDisabled]);

  const hasDisabledDescendants = useMemo(() => {
    return (node: TreeNode): boolean => {
      const leafNodes = getLeafTopicNodes(node);
      return leafNodes.some(leafNode => leafNode.data && isTopicDisabled(leafNode.data));
    };
  }, [getLeafTopicNodes, isTopicDisabled]);

  /** Every loaded descendant leaf is already on the student curriculum (checkbox locked on). */
  const isNodeCheckboxDisabled = useMemo(() => {
    return (node: TreeNode): boolean => {
      if (!isSelecting) return false;
      if (node.type === 'topic' && node.data && isTopicDisabled(node.data)) {
        return true;
      }
      const leafNodes = getLeafTopicNodes(node);
      if (leafNodes.length === 0) return false;
      return leafNodes.every((leaf) => leaf.data && isTopicDisabled(leaf.data));
    };
  }, [isSelecting, getLeafTopicNodes, isTopicDisabled]);

  return {
    isTopicSelected,
    getLeafTopicNodes,
    getCheckboxState,
    hasDisabledDescendants,
    isNodeCheckboxDisabled
  };
}
